import React, { useCallback, useState, useEffect } from 'react';
import { Form, Alert } from 'antd';
import {
  FormOutlined,
  EditOutlined,
  SafetyCertificateOutlined,
  KeyOutlined,
} from '@ant-design/icons';
import { useToast } from '../../../../hooks/toast';
import { AInput, AModal } from '../../../../components';
import api from '../../../../services/api';

interface IProps {
  open: boolean;
  close: () => void;
  config: any | null;
  onSuccess: () => void;
}

const NumberCreateEdit: React.FC<IProps> = ({
  open,
  close,
  config,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      if (config) {
        form.setFieldsValue({
          phone_number_id: config.phone_number_id,
          waba_id: config.waba_id,
          verify_token: config.verify_token,
          // access_token não é preenchido na edição por segurança
        });
      } else {
        form.setFieldsValue({
          verify_token: Math.random().toString(36).substring(7),
        });
      }
    } else {
      form.resetFields();
    }
  }, [open, config, form]);

  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // Se for edição e o token estiver vazio, removemos a chave para não sobrescrever com null no backend
      if (config?.id && !values.access_token) {
        delete values.access_token;
      }

      if (config?.id) {
        await api.put(`/whatsapp/config/${config.id}`, values);
      } else {
        await api.post('/whatsapp/config', values);
      }

      addToast({
        type: 'success',
        title: 'Sucesso',
        description: 'Configuração guardada com sucesso.',
      });
      onSuccess();
      close();
    } catch (err: any) {
      addToast({
        type: 'error',
        title: 'Erro',
        description:
          err.response?.data?.message || 'Falha ao guardar configuração.',
      });
    } finally {
      setLoading(false);
    }
  }, [form, config, addToast, onSuccess, close]);

  return (
    <AModal
      open={open}
      close={close}
      title={
        config ? (
          <>
            <EditOutlined /> Editar Credenciais Meta
          </>
        ) : (
          <>
            <FormOutlined /> Configurar API Oficial
          </>
        )
      }
      onOk={handleSubmit}
      confirmLoading={loading}
    >
      <Alert
        message="Credenciais da Meta"
        description="Obtenha estas informações no painel Meta for Developers (Cloud API)."
        type="info"
        showIcon
        style={{ marginBottom: 20 }}
      />

      <Form form={form} layout="vertical">
        <Form.Item
          label="Phone Number ID"
          name="phone_number_id" // ALINHADO COM BACKEND
          rules={[{ required: true, message: 'Obrigatório' }]}
        >
          <AInput placeholder="Ex: 105678901234567" />
        </Form.Item>

        <Form.Item
          label="WhatsApp Business Account ID (WABA)"
          name="waba_id" // ALINHADO COM BACKEND
          rules={[{ required: true, message: 'Obrigatório' }]}
        >
          <AInput placeholder="Ex: 209876543210987" />
        </Form.Item>

        <Form.Item
          label="System User Access Token"
          name="access_token" // ALINHADO COM BACKEND
          rules={[
            { required: !config, message: 'Obrigatório para novas ligações' },
          ]}
          help={
            config ? 'Deixe em branco para manter o token atual inalterado' : ''
          }
        >
          <AInput
            type="password"
            placeholder={
              config
                ? '*************************'
                : 'Token permanente gerado na Meta'
            }
            prefix={<SafetyCertificateOutlined />}
          />
        </Form.Item>

        <Form.Item
          label="Webhook Verify Token"
          name="verify_token" // ALINHADO COM BACKEND
          tooltip="Copie este token e cole na configuração de Webhook da Meta."
        >
          <AInput readOnly suffix={<KeyOutlined />} />
        </Form.Item>
      </Form>
    </AModal>
  );
};

export default NumberCreateEdit;
